/**
 * DetailsWimMegaDrawer - Mega menú WIM como drawer lateral (full height)
 * STANDALONE: no depende de DetailsDropdown/header.js para evitar conflictos.
 * Se desliza desde la izquierda o derecha según drawer_position.
 */
if (typeof customElements !== 'undefined' && !customElements.get('details-wim-mega-drawer')) {
  class DetailsWimMegaDrawer extends HTMLDetailsElement {
    constructor() {
      super();
      this.summaryElement = this.querySelector('summary');
      this._open = this.hasAttribute('open');
      this._boundHandleOutsideClick = this.handleOutsideClick.bind(this);
      this._boundHandleEscKeyPress = this.handleEscKeyPress.bind(this);

      if (this.summaryElement) {
        this.summaryElement.addEventListener('click', this.handleSummaryClick.bind(this));
      }
    }

    connectedCallback() {
      this.setupCol1ContentSwitch();
      this.setupContentMouseEnter();
      this.setupCol3Hover();
      this.setupCol3RestoreOnSwitch();
      requestAnimationFrame(() => this.updateHeaderSpacerHeight());

      this._drawerWidthObserver = new MutationObserver(() => {
        const width = this.style.getPropertyValue('--wim-drawer-width')
          || (this.dataset.drawerWidth ? `${this.dataset.drawerWidth}px` : '');
        if (width) {
          if (this._wimDrawerPortal) {
            this._wimDrawerPortal.style.setProperty('--wim-drawer-width', width);
          }
          if (this.contentElement) {
            this.contentElement.style.setProperty('--wim-drawer-width', width);
          }
        }
      });
      this._drawerWidthObserver.observe(this, { attributes: true, attributeFilter: ['style', 'data-drawer-width'] });
    }

    disconnectedCallback() {
      this.removeBodyOverlay();
      document.removeEventListener('click', this._boundHandleOutsideClick);
      document.removeEventListener('keydown', this._boundHandleEscKeyPress);
      if (this._col3RestoreObserver) {
        this._col3RestoreObserver.disconnect();
      }
      if (this._drawerWidthObserver) {
        this._drawerWidthObserver.disconnect();
      }
      this._contentMouseEnterHandlers?.forEach(({ content, handler }) => {
        content.removeEventListener('mouseenter', handler);
      });
      this._col1ToggleHandlers?.forEach(({ toggle, handler }) => {
        toggle.removeEventListener('mouseenter', handler);
      });
    }

    handleSummaryClick(e) {
      e.preventDefault();
      this.open = !this.open;
    }

    get open() {
      return this._open;
    }

    set open(value) {
      if (value === this._open) return;
      this._open = value;
      if (this.isConnected) {
        this.transition(value);
      } else {
        if (value) this.setAttribute('open', '');
        else this.removeAttribute('open');
      }
    }

    get drawerPosition() {
      return this.getAttribute('data-drawer-position') || 'left';
    }

    get contentElement() {
      return this._panelRef || this.querySelector('.wim-mega-drawer__panel') || this._wimDrawerPortal?.querySelector('.wim-mega-drawer__panel');
    }

    createBodyOverlay() {
      const panel = this.querySelector('.wim-mega-drawer__panel');
      if (this._wimDrawerPortal || !panel) return;
      this._panelRef = panel;
      const portal = document.createElement('div');
      portal.className = 'wim-mega-drawer__portal';
      portal.setAttribute('data-drawer-position', this.drawerPosition);
      const drawerWidth = this.style.getPropertyValue('--wim-drawer-width')
        || (this.dataset.drawerWidth ? `${this.dataset.drawerWidth}px` : '')
        || panel.style.getPropertyValue('--wim-drawer-width')
        || '50%';
      portal.style.setProperty('--wim-drawer-width', drawerWidth);
      panel.style.setProperty('--wim-drawer-width', drawerWidth);

      const overlay = document.createElement('div');
      overlay.className = 'wim-mega-drawer__body-overlay no-empty';
      overlay.setAttribute('aria-hidden', 'true');
      overlay.addEventListener('click', () => { this.open = false; });
      overlay.addEventListener('mousedown', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.open = false;
      });

      portal.appendChild(overlay);
      portal.appendChild(panel);
      document.body.appendChild(portal);
      this._wimDrawerPortal = portal;
      this._bodyOverlay = overlay;
    }

    removeBodyOverlay() {
      const panel = this._panelRef || this._wimDrawerPortal?.querySelector('.wim-mega-drawer__panel');
      if (this._wimDrawerPortal && this._wimDrawerPortal.parentNode && panel) {
        if (this._wimDrawerPortal.contains(panel)) {
          this.appendChild(panel);
        }
        this._wimDrawerPortal.parentNode.removeChild(this._wimDrawerPortal);
        this._wimDrawerPortal = null;
        this._bodyOverlay = null;
      }
    }

    handleOutsideClick(e) {
      if (!this._wimDrawerPortal) return;
      if (this.contains(e.target)) return;
      const portal = this._wimDrawerPortal;
      if (portal.contains(e.target)) {
        if (e.target === this._bodyOverlay || this._bodyOverlay.contains(e.target)) {
          this.open = false;
        }
        return;
      }
      this.open = false;
    }

    handleEscKeyPress(e) {
      if (e.code === 'Escape') {
        const openDrawer = document.querySelector('details.wim-mega-drawer[open]');
        if (openDrawer) openDrawer.open = false;
      }
    }

    updateHeaderSpacerHeight() {
      const stickyHeaderEl = document.querySelector('[is="sticky-header"]');
      const headerSection = stickyHeaderEl ? stickyHeaderEl.closest('.header-section') : document.querySelector('.header-section');
      const topbarSection = document.querySelector('.topbar-section');
      let height = 0;
      if (headerSection) {
        const topbarBottom = topbarSection ? Math.round(topbarSection.getBoundingClientRect().bottom) : 0;
        if (topbarBottom > 0) {
          height = Math.round(headerSection.getBoundingClientRect().bottom);
        } else {
          height = headerSection.offsetHeight;
        }
      }
      if (height <= 0) {
        const headerGroup = document.querySelector('.shopify-section-group-header-group');
        if (headerGroup) height = Math.round(headerGroup.offsetHeight);
      }
      if (height > 0) {
        document.documentElement.style.setProperty('--wim-header-spacer-height', `${height}px`);
      }
    }

    /**
     * Restablece las clases del header a su estado inicial sin scroll.
     * Se invoca al abrir y cerrar cuando el usuario está en el tope de página.
     */
    resetHeaderStateAtTop() {
      const headerSection = document.querySelector('.header-section');
      if (headerSection) {
        headerSection.classList.remove('header-scrolled');
      }
      const headerNavigation = document.querySelector('.header__bottom');
      if (headerNavigation) {
        headerNavigation.classList.remove('is-hide');
        headerNavigation.classList.remove('is-show');
      }
      document.body.classList.remove('header-pinned');
      document.body.classList.remove('is-hide-nav');
    }

    async transition(value) {
      // Captura la posición de scroll antes de alterar el DOM
      const wasAtTop = window.scrollY <= 10;

      if (value) {
        document.body.classList.add('wim-mega-drawer-open');
        this.updateHeaderSpacerHeight();
        
        // Turn hamburger into X
        document.querySelectorAll('.toggle-navigation-button').forEach(btn => {
          btn.setAttribute('aria-expanded', 'true');
        });

        this.setAttribute('open', '');
        if (this.summaryElement) this.summaryElement.setAttribute('open', '');
        if (this.contentElement) this.contentElement.setAttribute('open', '');
        this.createBodyOverlay();
        document.addEventListener('click', this._boundHandleOutsideClick);
        document.addEventListener('keydown', this._boundHandleEscKeyPress);

        // Al abrir arriba, overflow:hidden puede disparar un scroll/resize síncrono.
        // Se limpia el estado del header inmediatamente y en el siguiente frame.
        if (wasAtTop) {
          this.resetHeaderStateAtTop();
          requestAnimationFrame(() => {
            if (window.scrollY <= 10) this.resetHeaderStateAtTop();
          });
        }

        await this.showWithTransition();

        // Segunda salvaguarda tras finalizar la animación de transición
        if (wasAtTop && window.scrollY <= 10) {
          this.resetHeaderStateAtTop();
        }
      } else {
        document.removeEventListener('click', this._boundHandleOutsideClick);
        document.removeEventListener('keydown', this._boundHandleEscKeyPress);
        
        // Turn X back to hamburger
        document.querySelectorAll('.toggle-navigation-button').forEach(btn => {
          btn.setAttribute('aria-expanded', 'false');
        });

        await this.hideWithTransition();

        this.removeBodyOverlay();
        document.body.classList.remove('wim-mega-drawer-open');
        if (this.summaryElement) this.summaryElement.removeAttribute('open');
        if (this.contentElement) this.contentElement.removeAttribute('open');
        this.removeAttribute('open');

        // Limpia el estado del StickyHeader si el usuario está al tope de la página
        if (wasAtTop || window.scrollY <= 10) {
          this.resetHeaderStateAtTop();
          // Limpieza diferida por eventos de scroll remanentes tras restaurar overflow
          requestAnimationFrame(() => {
            if (window.scrollY <= 10) this.resetHeaderStateAtTop();
          });
        }
      }
    }

    async showWithTransition() {
      if (!this.contentElement) return Promise.resolve();
      const isLeft = this.drawerPosition === 'left';
      const fromX = isLeft ? '-100%' : '100%';
      return (window.FoxTheme?.Motion?.animate(
        this.contentElement,
        { transform: [`translateX(${fromX})`, 'translateX(0)'] },
        {
          duration: window.FoxTheme?.config?.motionReduced ? 0 : 0.5,
          easing: [0.7, 0, 0.2, 1],
        }
      )?.finished) ?? Promise.resolve();
    }

    async hideWithTransition() {
      if (!this.contentElement) return Promise.resolve();
      const isLeft = this.drawerPosition === 'left';
      const toX = isLeft ? '-100%' : '100%';
      return (window.FoxTheme?.Motion?.animate(
        this.contentElement,
        { transform: `translateX(${toX})` },
        {
          duration: window.FoxTheme?.config?.motionReduced ? 0 : 0.5,
          easing: [0.7, 0, 0.2, 1],
        }
      )?.finished) ?? Promise.resolve();
    }

    setupCol1ContentSwitch() {
      const menuSidebar = this.querySelector('menu-sidebar.wim-menu-sidebar');
      if (!menuSidebar) return;
      const toggles = menuSidebar.querySelectorAll('.menu-sidebar__toggle');
      const hideAll = () => {
        menuSidebar.querySelectorAll('.menu-sidebar__content').forEach((c) => {
          c.style.display = 'none';
        });
        toggles.forEach((t) => t.classList.remove('is-visible'));
      };
      const showOnly = (activeContent) => {
        menuSidebar.querySelectorAll('.menu-sidebar__content').forEach((c) => {
          c.style.display = c === activeContent ? 'flex' : 'none';
        });
      };
      this._col1ToggleHandlers = [];
      toggles.forEach((toggle) => {
        const content = toggle.nextElementSibling;
        if (!content?.classList.contains('menu-sidebar__content')) return;
        const handler = () => {
          toggles.forEach((t) => t.classList.remove('is-visible'));
          toggle.classList.add('is-visible');
          showOnly(content);
          this.clearCol3Selection(content);
          this.updateScrollability(content);
          const container = menuSidebar.closest('.mega-menu__wrapper');
          if (container && content) {
            container.style.setProperty('--sidebar-height', `${content.scrollHeight}px`);
          }
        };
        toggle.addEventListener('mouseenter', handler);
        this._col1ToggleHandlers.push({ toggle, handler });
      });
      hideAll();
    }

    setupContentMouseEnter() {
      const menuSidebar = this.querySelector('menu-sidebar.wim-menu-sidebar');
      if (!menuSidebar) return;
      const contents = menuSidebar.querySelectorAll('.menu-sidebar__content');
      this._contentMouseEnterHandlers = [];
      contents.forEach((content) => {
        const summary = content.previousElementSibling;
        if (!summary?.classList.contains('menu-sidebar__toggle')) return;
        const handler = () => {
          menuSidebar.querySelectorAll('.menu-sidebar__toggle').forEach((t) => t.classList.remove('is-visible'));
          summary.classList.add('is-visible');
          menuSidebar.querySelectorAll('.menu-sidebar__content').forEach((c) => {
            c.style.display = c === content ? 'flex' : 'none';
          });
          this.clearCol3Selection(content);
          this.updateScrollability(content);
          const container = menuSidebar.closest('.mega-menu__wrapper');
          if (container && content) {
            container.style.setProperty('--sidebar-height', `${content.scrollHeight}px`);
          }
        };
        content.addEventListener('mouseenter', handler);
        this._contentMouseEnterHandlers.push({ content, handler });
      });
    }

    setupCol3Hover() {
      const contents = this.querySelectorAll('.wim-mega-menu__content');
      contents.forEach((content) => {
        const targets = content.querySelectorAll('[data-col3-target]');
        const panes = content.querySelectorAll('[data-col3-pane]');
        const setActiveByIndex = (activeIndex) => {
          targets.forEach((t, j) => t.classList.toggle('is-active', j === activeIndex));
          panes.forEach((p, j) => p.classList.toggle('is-visible', j === activeIndex));
          this.updateScrollability(content);
        };
        content.addEventListener('mouseover', (e) => {
          const target = e.target.closest('[data-col3-target]');
          if (target) {
            const i = Array.from(targets).indexOf(target);
            if (i >= 0) setActiveByIndex(i);
          }
        });
        this.clearCol3Selection(content);
        this.updateScrollability(content);
      });
    }

    clearCol3Selection(content) {
      if (!content) return;
      const targets = Array.from(content.querySelectorAll('[data-col3-target]'));
      const panes = Array.from(content.querySelectorAll('[data-col3-pane]'));
      targets.forEach((target, index) => {
        target.classList.toggle('is-active', false);
      });
      panes.forEach((pane, index) => {
        pane.classList.toggle('is-visible', false);
      });
    }

    updateScrollability(content) {
      if (!content) return;
      const col2 = content.querySelector('.wim-mega-menu__col2');
      if (col2) {
        const isCol2Scrollable = col2.scrollHeight > col2.clientHeight + 1;
        col2.classList.toggle('is-scrollable', isCol2Scrollable);
      }

      const visiblePane = content.querySelector('.wim-mega-menu__col3-pane.is-visible');
      if (visiblePane) {
        const isPaneScrollable = visiblePane.scrollHeight > visiblePane.clientHeight + 1;
        visiblePane.classList.toggle('is-scrollable', isPaneScrollable);
      }
    }

    /**
     * Al cambiar de categoría (Ofertas → Frutas → Ofertas), el MenuSidebar del theme
     * cambia el contenido visible. Activar el primer pane de Col3 con items (metafield)
     * para que reaparezcan Pollo, Cordero, vacuno, etc. al volver a Ofertas.
     */
    setupCol3RestoreOnSwitch() {
      const menuSidebar = this.querySelector('menu-sidebar.wim-menu-sidebar');
      if (!menuSidebar) return;

      const resetCol3Panes = (summary) => {
        const content = summary?.nextElementSibling;
        if (!content) return;
        const targets = content.querySelectorAll('[data-col3-target]');
        const panes = content.querySelectorAll('.wim-mega-menu__col3-pane');
        panes.forEach((p) => p.classList.remove('is-visible'));
        targets.forEach((t) => t.classList.remove('is-active'));
      };

      const observer = new MutationObserver((mutations) => {
        for (const mutation of mutations) {
          if (mutation.type !== 'attributes' || mutation.attributeName !== 'class') continue;
          const el = mutation.target;
          if (el.matches?.('.menu-sidebar__toggle') && el.classList.contains('is-visible')) {
            resetCol3Panes(el);
            this.clearCol3Selection(el.nextElementSibling);
            break;
          }
        }
      });

      this._col3RestoreObserver = observer;
      observer.observe(menuSidebar, {
        subtree: true,
        attributeFilter: ['class'],
      });
    }

  }

  customElements.define('details-wim-mega-drawer', DetailsWimMegaDrawer, { extends: 'details' });
}

(function initWimDrawer() {
  function syncBodyClass() {
    const openDrawer = document.querySelector('details.wim-mega-drawer[open]');
    document.body.classList.toggle('wim-mega-drawer-open', !!openDrawer);
  }

  function observeDrawers() {
    document.querySelectorAll('details.wim-mega-drawer').forEach(function (el) {
      if (el._wimObserved) return;
      el._wimObserved = true;
      new MutationObserver(syncBodyClass).observe(el, { attributes: true, attributeFilter: ['open'] });
    });
  }

  function interceptToggleClick(e) {
    const openDrawer = document.querySelector('details.wim-mega-drawer[open]');
    if (!openDrawer) return;
    const target = e.target;
    const inHeaderTop = target.closest && target.closest('.header__top');
    if (inHeaderTop) {
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
      openDrawer.open = false;
    }
  }

  document.addEventListener('DOMContentLoaded', function () {
    document.addEventListener('mousedown', interceptToggleClick, true);
    document.addEventListener('click', interceptToggleClick, true);
    observeDrawers();
    syncBodyClass();

    document.addEventListener('shopify:section:load', function () {
      document.querySelectorAll('.wim-mega-drawer__portal').forEach(p => p.remove());
      document.body.classList.remove('wim-mega-drawer-open');
      observeDrawers();
    });
    document.addEventListener('shopify:block:select', function (e) {
      const blockEl = e.target.closest('details.wim-mega-drawer') || document.querySelector(`details.wim-mega-drawer#Mega-menu-item-${e.detail?.blockId}`);
      if (blockEl) {
        blockEl.open = true;
      }
    });
    document.addEventListener('shopify:block:deselect', function (e) {
      const blockEl = e.target.closest('details.wim-mega-drawer') || document.querySelector(`details.wim-mega-drawer#Mega-menu-item-${e.detail?.blockId}`);
      if (blockEl) {
        blockEl.open = false;
      }
    });
  });
})();
